import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Chapters.css';
import { chapterList } from '../data/chapterTemplates';

const Chapters = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('grid');

  const chapters = useMemo(() => chapterList, []);

  const filteredChapters = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return chapters;
    return chapters.filter((chapter) =>
      [chapter.name, chapter.campus, chapter.established]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [chapters, searchQuery]);

  return (
    <div className="chapters-page">

      {/* Hero */}
      <section className="chapters-hero">
        <p className="chapters-hero__eyebrow">University network</p>
        <h1>Chapters across the country.</h1>
        <p>
          Each Project LTF chapter adapts our national curriculum to its local community—pairing
          students with mentors who know the campus culture and admissions pathways firsthand.
        </p>
      </section>

      {/* Controls */}
      <div className="chapters-controls">
        <div className="chapters-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search chapters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="chapters-view-toggle">
          <button
            type="button"
            className={view === 'grid' ? 'is-active' : ''}
            onClick={() => setView('grid')}
            aria-label="Grid view"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
              <rect x="0" y="0" width="6" height="6" rx="1"/><rect x="9" y="0" width="6" height="6" rx="1"/>
              <rect x="0" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>
            </svg>
            Grid
          </button>
          <button
            type="button"
            className={view === 'list' ? 'is-active' : ''}
            onClick={() => setView('list')}
            aria-label="List view"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
              <rect x="0" y="1" width="15" height="2" rx="1"/><rect x="0" y="6.5" width="15" height="2" rx="1"/>
              <rect x="0" y="12" width="15" height="2" rx="1"/>
            </svg>
            List
          </button>
        </div>
      </div>

      {/* Empty state */}
      {filteredChapters.length === 0 ? (
        <div className="chapters-empty">
          <p>No chapters match "<strong>{searchQuery}</strong>".</p>
          <a href="mailto:hello@projectlevelthefield.com">Interested in launching one? →</a>
        </div>
      ) : view === 'grid' ? (

        /* Grid view */
        <div className="chapters-grid">
          {filteredChapters.map((chapter) => (
            <Link
              to={chapter.link}
              className={`chapter-card ${chapter.status === 'coming-soon' ? 'is-coming-soon' : ''}`}
              key={chapter.id}
            >
              <div className="chapter-card__photo">
                <img src={chapter.image} alt={chapter.name} loading="lazy" />
              </div>
              <div className="chapter-card__info">
                <div className="chapter-card__header">
                  <h3>{chapter.name}</h3>
                  <span className={`chapter-card__status ${chapter.status === 'active' ? 'is-active' : ''}`}>
                    {chapter.status === 'active' ? 'Active' : 'Coming soon'}
                  </span>
                </div>
                <p className="chapter-card__campus">{chapter.campus}</p>
                <p className="chapter-card__established">{chapter.established}</p>
              </div>
            </Link>
          ))}
        </div>

      ) : (

        /* List view */
        <div className="chapters-list">
          {filteredChapters.map((chapter) => (
            <Link
              to={chapter.link}
              className={`chapter-row ${chapter.status === 'coming-soon' ? 'is-coming-soon' : ''}`}
              key={`${chapter.id}-list`}
            >
              <div className="chapter-row__photo">
                <img src={chapter.image} alt={chapter.name} loading="lazy" />
              </div>
              <div className="chapter-row__info">
                <p className="chapter-row__name">{chapter.name}</p>
                <p className="chapter-row__campus">{chapter.campus}</p>
              </div>
              <span className={`chapter-card__status ${chapter.status === 'active' ? 'is-active' : ''}`}>
                {chapter.status === 'active' ? 'Active' : 'Coming soon'}
              </span>
              <span className="chapter-row__arrow">→</span>
            </Link>
          ))}
        </div>

      )}

      {/* Start a chapter CTA */}
      <section className="chapters-cta">
        <h2>Start a chapter</h2>
        <p>
          We support new founding teams with curriculum, training playbooks, and small launch
          grants. Introduce yourself and we'll schedule a short discovery call.
        </p>
        <a className="chapters-cta__button" href="mailto:hello@projectlevelthefield.com">
          Contact the expansion team
        </a>
      </section>

    </div>
  );
};

export default Chapters;
